/**
 * MaintenancePage.js  –  Work Orders · Preventive Maintenance · Cost Tracking
 * UI v2 — improved form UX: priority picker, category grid, section grouping
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Card, TabBar, Field, inputStyle, btnPrimary, btnSm, Empty, fmtKES } from '../components/UI';
import * as api from '../api/client';

const gold   = '#C9A84C';
const green  = '#2ecc71';
const red    = '#e74c3c';
const orange = '#f39c12';
const blue   = '#3498db';
const purple = '#9b59b6';

const PRIORITY = [
  { value: 'low',      label: 'Low',      color: blue,   icon: '🔵', desc: 'Non-urgent, can be scheduled' },
  { value: 'medium',   label: 'Medium',   color: gold,   icon: '🟡', desc: 'Should be done this week' },
  { value: 'high',     label: 'High',     color: orange, icon: '🟠', desc: 'Address within 24–48 hours' },
  { value: 'critical', label: 'Critical', color: red,    icon: '🔴', desc: 'Immediate attention required' },
];

const WO_CATEGORIES = [
  { value: 'electrical', label: '⚡ Electrical' },
  { value: 'plumbing',   label: '🚿 Plumbing' },
  { value: 'hvac',       label: '❄️ HVAC / AC' },
  { value: 'carpentry',  label: '🪚 Carpentry' },
  { value: 'painting',   label: '🎨 Painting' },
  { value: 'cleaning',   label: '🧹 Deep Clean' },
  { value: 'equipment',  label: '⚙️ Equipment' },
  { value: 'it',         label: '💻 IT / Tech' },
  { value: 'pool',       label: '🏊 Pool' },
  { value: 'grounds',    label: '🌿 Grounds' },
  { value: 'other',      label: '📦 Other' },
];

const PM_FREQ = [
  { value: 'daily',     label: '📆 Daily',         desc: 'Every day' },
  { value: 'weekly',    label: '📅 Weekly',         desc: 'Once a week' },
  { value: 'monthly',   label: '🗓️ Monthly',       desc: 'Once a month' },
  { value: 'quarterly', label: '📊 Quarterly',      desc: 'Every 3 months' },
  { value: 'biannual',  label: '📈 Bi-Annual',      desc: 'Every 6 months' },
  { value: 'annual',    label: '🏆 Annual',          desc: 'Once a year' },
];

const PRIORITY_COLOR = { low: blue, medium: gold, high: orange, critical: red };
const STATUS_COLOR   = { open: '#888', assigned: blue, in_progress: orange, completed: green, cancelled: '#444' };
const STATUS_ICON    = { open: '⬜', assigned: '🔵', in_progress: '🟠', completed: '✅', cancelled: '⬛' };

const blankWO   = { title:'', description:'', category:'electrical', priority:'medium', location:'', asset:'', scheduled_date:'', estimated_hours:'', estimated_cost:'' };
const blankPM   = { title:'', description:'', asset:'', frequency:'monthly', next_due:'', estimated_cost:'', assigned_to:'' };
const blankCost = { work_order:'', description:'', cost_type:'parts', amount:'', vendor:'' };

/* ─── Shared modal shell ──────────────────────────────────────────────── */
function Modal({ onClose, children, width = 600 }) {
  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.78)', zIndex:999, display:'flex', alignItems:'center', justifyContent:'center', overflow:'auto', padding:20 }}>
      <div style={{ background:'var(--surface)', borderRadius:16, width, maxWidth:'95vw', boxShadow:'0 28px 90px rgba(0,0,0,0.65)', border:'1px solid rgba(201,168,76,0.15)', animation:'slideUp 0.18s ease' }}>
        {children}
      </div>
      <style>{`@keyframes slideUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

function ModalHeader({ icon, title, subtitle, onClose }) {
  return (
    <div style={{ padding:'22px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
      <div>
        <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
          <span style={{ fontSize:22 }}>{icon}</span>
          <h3 style={{ margin:0, color:gold, fontSize:18, fontWeight:700 }}>{title}</h3>
        </div>
        {subtitle && <div style={{ color:'var(--textMuted)', fontSize:13, paddingLeft:32 }}>{subtitle}</div>}
      </div>
      <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--textMuted)', cursor:'pointer', fontSize:20, lineHeight:1, padding:4 }}>✕</button>
    </div>
  );
}

function ModalFooter({ onSave, onCancel, saveLabel='Save', saving=false, errorCount=0 }) {
  return (
    <div style={{ padding:'16px 24px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', gap:10, justifyContent:'flex-end', alignItems:'center' }}>
      {errorCount > 0 && (
        <div style={{
          marginRight:'auto', display:'flex', alignItems:'center', gap:6,
          fontSize:12, color:'#ef4444', fontWeight:600,
          background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)',
          borderRadius:6, padding:'4px 10px',
        }}>
          <span>⚠</span>
          <span>{errorCount} field{errorCount > 1 ? 's need' : ' needs'} attention</span>
        </div>
      )}
      <button onClick={onCancel} style={{ ...btnSm, minWidth:90 }}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={{ ...btnPrimary, minWidth:150, opacity:saving?0.7:1 }}>
        {saving ? '⟳ Saving…' : saveLabel}
      </button>
    </div>
  );
}

function SectionLabel({ icon, title }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 0 8px', borderBottom:`1px solid rgba(201,168,76,0.18)`, marginBottom:14, gridColumn:'1/-1' }}>
      <span style={{ fontSize:14 }}>{icon}</span>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.09em', color:gold, textTransform:'uppercase' }}>{title}</span>
    </div>
  );
}

const iStyle = { ...inputStyle, borderRadius:8, padding:'10px 12px', fontSize:14 };
const iStyleErr = { ...iStyle, borderColor:'#ef4444', background:'rgba(239,68,68,0.04)' };

/* ─── Error banner ─────────────────────────────────────────────────────── */
function ErrorBanner({ errors }) {
  const general = errors?._general || errors?.non_field_errors || errors?.detail;
  if (!general) return null;
  return (
    <div style={{
      marginBottom:16, padding:'10px 14px', borderRadius:8,
      background:'rgba(239,68,68,0.07)', border:'1.5px solid rgba(239,68,68,0.25)',
      display:'flex', alignItems:'flex-start', gap:9, fontSize:13, color:'#ef4444',
    }}>
      <span style={{ fontSize:15, flexShrink:0 }}>⚠️</span>
      <span>{general}</span>
    </div>
  );
}

/* ─── Priority button picker ──────────────────────────────────────────── */
function PriorityPicker({ value, onChange }) {
  return (
    <div>
      <Field label="Priority" required hint="Sets urgency and response time">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8 }}>
          {PRIORITY.map(p => (
            <button key={p.value} onClick={() => onChange(p.value)} style={{
              padding:'10px 6px', borderRadius:10, border:'2px solid',
              borderColor: value===p.value ? p.color : 'var(--borderMid)',
              background: value===p.value ? p.color+'18' : 'var(--bgCard)',
              cursor:'pointer', textAlign:'center', transition:'all 0.15s',
            }}>
              <div style={{ fontSize:18, marginBottom:4 }}>{p.icon}</div>
              <div style={{ fontSize:11, fontWeight:700, color:value===p.value?p.color:'var(--textMuted)' }}>{p.label}</div>
            </button>
          ))}
        </div>
        {value && (
          <div style={{ marginTop:6, fontSize:11, color:PRIORITY_COLOR[value], fontStyle:'italic' }}>
            {PRIORITY.find(p=>p.value===value)?.desc}
          </div>
        )}
      </Field>
    </div>
  );
}

/* ─── Category grid picker ────────────────────────────────────────────── */
function CategoryPicker({ value, onChange }) {
  return (
    <div>
      <Field label="Category" required>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
          {WO_CATEGORIES.map(c => (
            <button key={c.value} onClick={() => onChange(c.value)} style={{
              padding:'8px 6px', borderRadius:8, border:'1.5px solid',
              borderColor: value===c.value ? gold : 'var(--borderMid)',
              background: value===c.value ? gold+'18' : 'var(--bgCard)',
              color: value===c.value ? gold : 'var(--textMuted)',
              cursor:'pointer', fontSize:11, fontWeight:600, textAlign:'center', transition:'all 0.12s',
            }}>{c.label}</button>
          ))}
        </div>
      </Field>
    </div>
  );
}

/* ─── Frequency picker for PM ─────────────────────────────────────────── */
function FrequencyPicker({ value, onChange }) {
  return (
    <div>
      <Field label="Frequency" required hint="How often this task recurs">
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
          {PM_FREQ.map(f => (
            <button key={f.value} onClick={() => onChange(f.value)} style={{
              padding:'9px 6px', borderRadius:8, border:'1.5px solid',
              borderColor: value===f.value ? gold : 'var(--borderMid)',
              background: value===f.value ? gold+'18' : 'var(--bgCard)',
              color: value===f.value ? gold : 'var(--textMuted)',
              cursor:'pointer', fontSize:11, fontWeight:600, textAlign:'center', transition:'all 0.12s',
            }}>
              <div style={{ fontSize:13 }}>{f.label.split(' ')[0]}</div>
              <div>{f.label.split(' ').slice(1).join(' ')}</div>
            </button>
          ))}
        </div>
      </Field>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */

export default function MaintenancePage({ notify, userRole }) {
  const [tab, setTab]         = useState('workorders');
  const [workOrders, setWOs]  = useState([]);
  const [pmSchedules, setPMs] = useState([]);
  const [assets, setAssets]   = useState([]);
  const [vendors, setVendors] = useState([]);
  const [staff, setStaff]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showWOForm, setShowWOForm]     = useState(false);
  const [woForm, setWoForm]             = useState(blankWO);
  const [savingWO, setSavingWO]         = useState(false);
  const [woErrors, setWoErrors]         = useState({});

  const [showPMForm, setShowPMForm]     = useState(false);
  const [pmForm, setPmForm]             = useState(blankPM);
  const [savingPM, setSavingPM]         = useState(false);
  const [pmErrors, setPmErrors]         = useState({});

  const [showCostForm, setShowCostForm] = useState(false);
  const [costForm, setCostForm]         = useState(blankCost);
  const [savingCost, setSavingCost]     = useState(false);
  const [costErrors, setCostErrors]     = useState({});

  const [woFilter, setWoFilter] = useState({ status:'', priority:'', category:'' });

  const canEdit = ['admin','accountant','maintenance'].includes(userRole) || !userRole;

  const wf = (k,v) => setWoForm(p => ({...p,[k]:v}));
  const pmf = (k,v) => setPmForm(p => ({...p,[k]:v}));
  const cf = (k,v) => setCostForm(p => ({...p,[k]:v}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [woRes, pmRes, assRes, vRes, sRes, sumRes] = await Promise.all([
        api.getWorkOrders(),
        api.getPMSchedules(),
        api.getAssets ? api.getAssets() : Promise.resolve({data:[]}),
        api.getVendors(),
        api.getUsers ? api.getUsers() : Promise.resolve({data:[]}),
        api.getMaintenanceSummary(),
      ]);
      setWOs(woRes.data.results || woRes.data || []);
      setPMs(pmRes.data.results || pmRes.data || []);
      setAssets(assRes.data.results || assRes.data || []);
      setVendors(vRes.data.results || vRes.data || []);
      setStaff(sRes.data.results || sRes.data || []);
      setSummary(sumRes.data);
    } catch { notify?.('Failed to load maintenance data','error'); }
    finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const extractApiErrors = (err) => {
    const data = err?.response?.data;
    if (!data) return {};
    if (typeof data === 'string') return { _general: data };
    if (typeof data === 'object') {
      const mapped = {};
      Object.entries(data).forEach(([k, v]) => { mapped[k] = Array.isArray(v) ? v[0] : String(v); });
      return mapped;
    }
    return {};
  };

  const saveWO = async () => {
    const errs = {};
    if (!woForm.title?.trim()) errs.title = 'Title is required';
    if (!woForm.description?.trim()) errs.description = 'Description is required';
    if (Object.keys(errs).length) { setWoErrors(errs); return; }
    setWoErrors({});
    setSavingWO(true);
    try {
      await api.createWorkOrder({ ...woForm, asset: woForm.asset || null });
      notify?.('Work order created');
      setShowWOForm(false); setWoForm(blankWO); load();
    } catch(err) {
      const apiErrs = extractApiErrors(err);
      if (Object.keys(apiErrs).length) { setWoErrors(apiErrs); notify?.('Please fix the highlighted fields', 'error'); }
      else notify?.('Failed to create work order','error');
    }
    finally { setSavingWO(false); }
  };

  const savePM = async () => {
    const errs = {};
    if (!pmForm.title?.trim()) errs.title = 'Title is required';
    if (!pmForm.next_due) errs.next_due = 'Next due date is required';
    if (Object.keys(errs).length) { setPmErrors(errs); return; }
    setPmErrors({});
    setSavingPM(true);
    try {
      await api.createPMSchedule({ ...pmForm, asset:pmForm.asset||null, assigned_to:pmForm.assigned_to||null });
      notify?.('PM schedule created');
      setShowPMForm(false); setPmForm(blankPM); load();
    } catch(err) {
      const apiErrs = extractApiErrors(err);
      if (Object.keys(apiErrs).length) { setPmErrors(apiErrs); notify?.('Please fix the highlighted fields', 'error'); }
      else notify?.('Failed to create PM schedule','error');
    }
    finally { setSavingPM(false); }
  };

  const saveCost = async () => {
    const errs = {};
    if (!costForm.work_order) errs.work_order = 'Work order is required';
    if (!costForm.description?.trim()) errs.description = 'Description is required';
    if (!costForm.amount || isNaN(Number(costForm.amount)) || Number(costForm.amount) <= 0) errs.amount = 'Enter a valid positive amount';
    if (Object.keys(errs).length) { setCostErrors(errs); return; }
    setCostErrors({});
    setSavingCost(true);
    try {
      await api.createMaintenanceCost({ ...costForm, vendor:costForm.vendor||null });
      notify?.('Cost recorded');
      setShowCostForm(false); setCostForm(blankCost); load();
    } catch(err) {
      const apiErrs = extractApiErrors(err);
      if (Object.keys(apiErrs).length) { setCostErrors(apiErrs); notify?.('Please fix the highlighted fields', 'error'); }
      else notify?.('Failed to record cost','error');
    }
    finally { setSavingCost(false); }
  };

  const updateStatus = async (id, status) => {
    try { await api.updateWOStatus(id, status); notify?.(`Work order ${status.replace('_',' ')}`); load(); }
    catch { notify?.('Failed to update status','error'); }
  };

  const markPMDone = async (id) => {
    try { await api.markPMDone(id); notify?.('PM marked done — next due date advanced'); load(); }
    catch { notify?.('Failed to update PM','error'); }
  };

  const filteredWOs = workOrders.filter(wo => {
    if (woFilter.status   && wo.status   !== woFilter.status)   return false;
    if (woFilter.priority && wo.priority !== woFilter.priority) return false;
    if (woFilter.category && wo.category !== woFilter.category) return false;
    return true;
  });

  const today = new Date().toISOString().split('T')[0];
  const overdueWOs = workOrders.filter(wo => wo.scheduled_date && wo.scheduled_date < today && !['completed','cancelled'].includes(wo.status));
  const overduePMs = pmSchedules.filter(pm => pm.is_active && pm.next_due < today);

  const tabs = [
    { id:'workorders', label:`🔧 Work Orders${overdueWOs.length>0?` (${overdueWOs.length} overdue)`:''}` },
    { id:'pm',         label:`📅 Prev. Maint.${overduePMs.length>0?` (${overduePMs.length} due)`:''}` },
    { id:'costs',      label:'💰 Cost Tracking' },
    { id:'summary',    label:'📊 Summary' },
  ];

  return (
    <div style={{ padding:'0 0 40px' }}>
      <PageHeader icon="🔧" title="Maintenance"
        sub="Work Orders · Preventive Maintenance · Asset Costs"
        action={
          <div style={{ display:'flex', gap:8 }}>
            {tab==='workorders' && <button onClick={() => { setWoForm(blankWO); setShowWOForm(true); }} style={btnPrimary}>+ Work Order</button>}
            {tab==='pm'         && <button onClick={() => { setPmForm(blankPM); setShowPMForm(true); }} style={btnPrimary}>+ PM Schedule</button>}
            {tab==='costs'      && <button onClick={() => { setCostForm(blankCost); setShowCostForm(true); }} style={btnPrimary}>+ Record Cost</button>}
          </div>
        }
      />
      <div style={{ padding:'0 20px 12px' }}><TabBar tabs={tabs} active={tab} onChange={setTab} /></div>

      {/* ══ WORK ORDER FORM MODAL ════════════════════════════════════════ */}
      {showWOForm && (
        <Modal onClose={() => { setShowWOForm(false); setWoErrors({}); }} width={660}>
          <ModalHeader icon="🔧" title="New Work Order"
            subtitle="Log a maintenance task — only Title and Description are required"
            onClose={() => { setShowWOForm(false); setWoErrors({}); }} />
          <div style={{ padding:'20px 24px', maxHeight:'66vh', overflowY:'auto' }}>
            <ErrorBanner errors={woErrors} />

            {/* ── Priority ── */}
            <div style={{ marginBottom: 20 }}>
              <PriorityPicker value={woForm.priority} onChange={v => wf('priority',v)} />
            </div>

            {/* ── Task Details card ── */}
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'16px 18px', marginBottom:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:14, paddingBottom:10, borderBottom:'1px solid rgba(201,168,76,0.15)' }}>
                <span>📋</span>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.09em', color:gold, textTransform:'uppercase' }}>Task Details</span>
              </div>
              <Field label="Work Order Title" required error={woErrors.title} hint={!woErrors.title ? 'Brief, descriptive summary' : undefined}>
                <input value={woForm.title} onChange={e => { wf('title',e.target.value); setWoErrors(p => ({...p, title:''})); }}
                  placeholder="e.g. AC unit not cooling — Room 204" autoFocus
                  style={{ ...(woErrors.title ? iStyleErr : iStyle), fontWeight:600, fontSize:15 }} />
              </Field>
              <div style={{ marginTop:12 }}>
                <Field label="Detailed Description" required error={woErrors.description} hint={!woErrors.description ? 'What is the problem? What needs to be done?' : undefined}>
                  <textarea value={woForm.description} onChange={e => { wf('description',e.target.value); setWoErrors(p => ({...p, description:''})); }}
                    rows={3} placeholder="e.g. Guest reported AC not cooling since yesterday. Unit is running but blowing warm air."
                    style={{ ...(woErrors.description ? iStyleErr : iStyle), resize:'vertical' }} />
                </Field>
              </div>
              <div style={{ marginTop:12 }}>
                <CategoryPicker value={woForm.category} onChange={v => wf('category',v)} />
              </div>
            </div>

            {/* ── Location & Asset card ── */}
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'16px 18px', marginBottom:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:14, paddingBottom:10, borderBottom:'1px solid rgba(201,168,76,0.15)' }}>
                <span>📍</span>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.09em', color:gold, textTransform:'uppercase' }}>Location & Asset</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Field label="Location / Room" hint="Where is the issue?">
                  <input value={woForm.location} onChange={e => wf('location',e.target.value)}
                    placeholder="e.g. Room 204, Kitchen, Reception" style={iStyle} />
                </Field>
                <Field label="Asset (optional)" hint="Link to a specific asset">
                  <select value={woForm.asset} onChange={e => wf('asset',e.target.value)} style={{ ...iStyle, cursor:'pointer' }}>
                    <option value="">— No specific asset —</option>
                    {assets.map(a => <option key={a.id} value={a.id}>{a.asset_code} – {a.name}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            {/* ── Schedule & Estimates card ── */}
            <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'16px 18px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:14, paddingBottom:10, borderBottom:'1px solid rgba(201,168,76,0.15)' }}>
                <span>📅</span>
                <span style={{ fontSize:11, fontWeight:700, letterSpacing:'0.09em', color:gold, textTransform:'uppercase' }}>Schedule & Estimates</span>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
                <Field label="Scheduled Date" hint="When should this be done?">
                  <input type="date" value={woForm.scheduled_date} onChange={e => wf('scheduled_date',e.target.value)} style={iStyle} />
                </Field>
                <Field label="Est. Hours" hint="Technician time">
                  <input type="number" min="0" step="0.5" value={woForm.estimated_hours} onChange={e => wf('estimated_hours',e.target.value)}
                    placeholder="e.g. 2.5" style={iStyle} />
                </Field>
                <Field label="Est. Cost (KES)" hint="Parts + labour estimate">
                  <input type="number" min="0" step="1" value={woForm.estimated_cost} onChange={e => wf('estimated_cost',e.target.value)}
                    placeholder="0" style={iStyle} />
                </Field>
              </div>
            </div>
          </div>
          <ModalFooter onSave={saveWO} onCancel={() => { setShowWOForm(false); setWoErrors({}); }} saveLabel="🔧 Create Work Order" saving={savingWO}
            errorCount={Object.values(woErrors).filter(Boolean).length} />
        </Modal>
      )}

      {/* ══ PM SCHEDULE FORM MODAL ══════════════════════════════════════ */}
      {showPMForm && (
        <Modal onClose={() => { setShowPMForm(false); setPmErrors({}); }} width={580}>
          <ModalHeader icon="📅" title="New PM Schedule"
            subtitle="Create a recurring preventive maintenance task"
            onClose={() => { setShowPMForm(false); setPmErrors({}); }} />
          <div style={{ padding:'20px 24px', maxHeight:'64vh', overflowY:'auto' }}>
            <ErrorBanner errors={pmErrors} />

            {/* Frequency — most structural choice first */}
            <div style={{ marginBottom:20 }}>
              <FrequencyPicker value={pmForm.frequency} onChange={v => pmf('frequency',v)} />
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <SectionLabel icon="📋" title="Task Details" />
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="PM Task Title" required error={pmErrors.title}>
                  <input value={pmForm.title} onChange={e => { pmf('title',e.target.value); setPmErrors(p=>({...p,title:''})); }} autoFocus
                    placeholder="e.g. HVAC Filter Replacement"
                    style={{ ...(pmErrors.title ? iStyleErr : iStyle), fontWeight:600 }} />
                </Field>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Description / Instructions" hint="What should the technician do?">
                  <textarea value={pmForm.description} onChange={e => pmf('description',e.target.value)}
                    rows={3} placeholder="e.g. Replace HVAC filters in all AC units. Check refrigerant levels. Clean condenser coils. Log results."
                    style={{ ...iStyle, resize:'vertical' }} />
                </Field>
              </div>

              <SectionLabel icon="📍" title="Asset & Assignment" />
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Asset (optional)" hint="Which asset does this PM apply to?">
                  <select value={pmForm.asset} onChange={e => pmf('asset',e.target.value)} style={{ ...iStyle, cursor:'pointer' }}>
                    <option value="">— All / No specific asset —</option>
                    {assets.map(a => <option key={a.id} value={a.id}>{a.asset_code} – {a.name}</option>)}
                  </select>
                </Field>
              </div>
              {staff.length > 0 && (
                <div style={{ gridColumn:'1/-1' }}>
                  <Field label="Assign To (optional)">
                    <select value={pmForm.assigned_to||''} onChange={e => pmf('assigned_to',e.target.value)} style={{ ...iStyle, cursor:'pointer' }}>
                      <option value="">— Unassigned —</option>
                      {staff.filter(s=>['maintenance','admin'].includes(s.role)).map(s=>(
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                  </Field>
                </div>
              )}

              <SectionLabel icon="📅" title="Schedule & Cost" />
              <div>
                <Field label="Next Due Date" required error={pmErrors.next_due} hint={!pmErrors.next_due ? 'When is the first / next occurrence?' : undefined}>
                  <input type="date" value={pmForm.next_due} onChange={e => { pmf('next_due',e.target.value); setPmErrors(p=>({...p,next_due:''})); }}
                    style={pmErrors.next_due ? iStyleErr : iStyle} />
                </Field>
              </div>
              <div>
                <Field label="Est. Cost per Run (KES)">
                  <input type="number" min="0" step="1" value={pmForm.estimated_cost} onChange={e => pmf('estimated_cost',e.target.value)}
                    placeholder="0" style={iStyle} />
                </Field>
              </div>
            </div>
          </div>
          <ModalFooter onSave={savePM} onCancel={() => { setShowPMForm(false); setPmErrors({}); }} saveLabel="📅 Save PM Schedule" saving={savingPM}
            errorCount={Object.values(pmErrors).filter(Boolean).length} />
        </Modal>
      )}

      {/* ══ COST FORM MODAL ═════════════════════════════════════════════ */}
      {showCostForm && (
        <Modal onClose={() => { setShowCostForm(false); setCostErrors({}); }} width={480}>
          <ModalHeader icon="💰" title="Record Maintenance Cost"
            subtitle="Log parts, labour or contractor expenses against a work order"
            onClose={() => { setShowCostForm(false); setCostErrors({}); }} />
          <div style={{ padding:'20px 24px' }}>
            <ErrorBanner errors={costErrors} />

            <Field label="Work Order" required error={costErrors.work_order} hint={!costErrors.work_order ? 'Which work order does this cost belong to?' : undefined}>
              <select value={costForm.work_order} onChange={e => { cf('work_order',e.target.value); setCostErrors(p=>({...p,work_order:''})); }}
                style={{ ...(costErrors.work_order ? iStyleErr : iStyle), cursor:'pointer' }}>
                <option value="">— Select work order —</option>
                {workOrders.filter(w=>!['cancelled'].includes(w.status)).map(w=>(
                  <option key={w.id} value={w.id}>{w.ref} — {w.title.slice(0,45)}</option>
                ))}
              </select>
            </Field>

            {/* Cost type selector */}
            <Field label="Cost Type" required>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:4 }}>
                {[
                  { value:'labour',     label:'👷 Labour' },
                  { value:'parts',      label:'🔩 Parts' },
                  { value:'contractor', label:'🏗️ Contractor' },
                  { value:'other',      label:'📦 Other' },
                ].map(t => (
                  <button key={t.value} onClick={() => cf('cost_type',t.value)} style={{
                    padding:'10px 6px', borderRadius:8, border:'1.5px solid',
                    borderColor: costForm.cost_type===t.value ? gold : 'var(--borderMid)',
                    background: costForm.cost_type===t.value ? gold+'18' : 'var(--bgCard)',
                    color: costForm.cost_type===t.value ? gold : 'var(--textMuted)',
                    cursor:'pointer', fontSize:11, fontWeight:600, textAlign:'center', transition:'all 0.12s',
                  }}>{t.label}</button>
                ))}
              </div>
            </Field>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginTop:4 }}>
              <div style={{ gridColumn:'1/-1' }}>
                <Field label="Description" required error={costErrors.description} hint={!costErrors.description ? 'What was purchased / done?' : undefined}>
                  <input value={costForm.description} onChange={e => { cf('description',e.target.value); setCostErrors(p=>({...p,description:''})); }}
                    placeholder="e.g. Replacement AC compressor unit"
                    style={costErrors.description ? iStyleErr : iStyle} autoFocus />
                </Field>
              </div>
              <div>
                <Field label="Amount (KES)" required error={costErrors.amount}>
                  <input type="number" min="0" step="1" value={costForm.amount}
                    onChange={e => { cf('amount',e.target.value); setCostErrors(p=>({...p,amount:''})); }}
                    placeholder="0" style={costErrors.amount ? iStyleErr : iStyle} />
                </Field>
              </div>
              <div>
                <Field label="Supplier / Vendor" hint="Optional — who supplied it?">
                  <select value={costForm.vendor} onChange={e => cf('vendor',e.target.value)} style={{ ...iStyle, cursor:'pointer' }}>
                    <option value="">— None —</option>
                    {vendors.filter(v=>v.is_active).map(v=><option key={v.id} value={v.id}>{v.name}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          </div>
          <ModalFooter onSave={saveCost} onCancel={() => { setShowCostForm(false); setCostErrors({}); }} saveLabel="Record Cost" saving={savingCost}
            errorCount={Object.values(costErrors).filter(Boolean).length} />
        </Modal>
      )}

      {/* ══ WORK ORDERS TAB ══════════════════════════════════════════════ */}
      {tab === 'workorders' && (
        <div style={{ padding:'0 20px' }}>
          {overdueWOs.length > 0 && (
            <div style={{ background:`${red}14`, border:`1px solid ${red}38`, borderRadius:8, padding:'10px 16px', marginBottom:14, display:'flex', gap:10, alignItems:'center' }}>
              <span style={{ fontSize:18 }}>⚠️</span>
              <span style={{ color:red, fontWeight:700 }}>{overdueWOs.length} work order{overdueWOs.length>1?'s':''} overdue</span>
              <span style={{ color:'var(--textMuted)', fontSize:13 }}>— action required</span>
            </div>
          )}

          {/* Filters */}
          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
            {[
              ['status','Status',[['','All Status'],['open','⬜ Open'],['assigned','🔵 Assigned'],['in_progress','🟠 In Progress'],['completed','✅ Completed'],['cancelled','⬛ Cancelled']]],
              ['priority','Priority',[['','All Priority'],['critical','🔴 Critical'],['high','🟠 High'],['medium','🟡 Medium'],['low','🔵 Low']]],
              ['category','Category',[['','All Categories'],...WO_CATEGORIES.map(c=>[c.value,c.label])]],
            ].map(([field,label,opts]) => (
              <select key={field} value={woFilter[field]} onChange={e => setWoFilter(p=>({...p,[field]:e.target.value}))}
                style={{ ...iStyle, width:field==='category'?180:160 }}>
                {opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ))}
            {(woFilter.status||woFilter.priority||woFilter.category) && (
              <button onClick={() => setWoFilter({status:'',priority:'',category:''})} style={{ ...btnSm, fontSize:12 }}>✕ Clear</button>
            )}
          </div>

          {filteredWOs.length===0 ? <Empty text="No work orders found" /> : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {filteredWOs.map(wo => {
                const isOverdue = wo.scheduled_date && wo.scheduled_date < today && !['completed','cancelled'].includes(wo.status);
                const catObj = WO_CATEGORIES.find(c=>c.value===wo.category);
                return (
                  <div key={wo.id} style={{
                    background:'var(--surface)', borderRadius:12, overflow:'hidden',
                    borderLeft:`4px solid ${PRIORITY_COLOR[wo.priority]||gold}`,
                    border:`1px solid rgba(255,255,255,0.05)`,
                    borderLeftWidth:4, borderLeftColor:PRIORITY_COLOR[wo.priority]||gold,
                  }}>
                    <div style={{ padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14 }}>
                      <div style={{ flex:1, minWidth:0 }}>
                        {/* Header row */}
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:6 }}>
                          <span style={{ fontWeight:700, color:gold, fontSize:13, fontFamily:'monospace' }}>{wo.ref}</span>
                          <span style={{ background:PRIORITY_COLOR[wo.priority]+'22', color:PRIORITY_COLOR[wo.priority], borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:700 }}>
                            {PRIORITY.find(p=>p.value===wo.priority)?.icon} {wo.priority}
                          </span>
                          <span style={{ background:STATUS_COLOR[wo.status]+'22', color:STATUS_COLOR[wo.status], borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:600 }}>
                            {STATUS_ICON[wo.status]} {wo.status.replace('_',' ')}
                          </span>
                          {catObj && <span style={{ background:'rgba(255,255,255,0.06)', borderRadius:20, padding:'2px 9px', fontSize:11, color:'var(--textMuted)' }}>{catObj.label}</span>}
                          {isOverdue && <span style={{ background:red+'22', color:red, borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:700 }}>⚠ Overdue</span>}
                        </div>

                        {/* Title */}
                        <div style={{ fontWeight:700, fontSize:15, color:'var(--text)', marginBottom:4 }}>{wo.title}</div>

                        {/* Meta row */}
                        <div style={{ display:'flex', gap:14, flexWrap:'wrap', fontSize:12, color:'var(--textMuted)', marginBottom:4 }}>
                          {wo.location && <span>📍 {wo.location}</span>}
                          {wo.asset_name && <span>⚙️ {wo.asset_name}</span>}
                          {wo.scheduled_date && <span style={{ color:isOverdue?red:undefined }}>📅 {wo.scheduled_date}</span>}
                          {wo.reported_by_name && <span>👤 {wo.reported_by_name}</span>}
                        </div>

                        {wo.description && (
                          <div style={{ color:'var(--textMuted)', fontSize:12, lineHeight:1.5, marginBottom:6 }}>
                            {wo.description.slice(0,160)}{wo.description.length>160?'…':''}
                          </div>
                        )}

                        {/* Cost summary */}
                        {(wo.estimated_cost > 0 || wo.actual_cost > 0) && (
                          <div style={{ display:'flex', gap:16, fontSize:12 }}>
                            {wo.estimated_cost > 0 && <span style={{ color:'var(--textMuted)' }}>Est: <strong style={{ color:'var(--text)' }}>{fmtKES(wo.estimated_cost)}</strong></span>}
                            {wo.actual_cost > 0 && <span style={{ color:'var(--textMuted)' }}>Actual: <strong style={{ color:wo.actual_cost>wo.estimated_cost&&wo.estimated_cost>0?red:green }}>{fmtKES(wo.actual_cost)}</strong></span>}
                          </div>
                        )}

                        {/* Inline cost pills */}
                        {wo.cost_items?.length > 0 && (
                          <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:5 }}>
                            {wo.cost_items.map(c=>(
                              <span key={c.id} style={{ background:'#1a1a1a', borderRadius:20, padding:'2px 10px', fontSize:11, color:'var(--textMuted)' }}>
                                {c.description} — {fmtKES(c.amount)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div style={{ display:'flex', flexDirection:'column', gap:6, minWidth:130, flexShrink:0 }}>
                        {wo.status==='open' && <>
                          <button onClick={() => updateStatus(wo.id,'in_progress')} style={{ ...btnSm, background:orange+'22', color:orange, border:`1px solid ${orange}44`, fontSize:11 }}>▶ Start</button>
                          <button onClick={() => updateStatus(wo.id,'cancelled')} style={{ ...btnSm, background:'rgba(255,255,255,0.04)', color:'var(--textMuted)', fontSize:11 }}>Cancel</button>
                        </>}
                        {wo.status==='assigned' && (
                          <button onClick={() => updateStatus(wo.id,'in_progress')} style={{ ...btnSm, background:orange+'22', color:orange, border:`1px solid ${orange}44`, fontSize:11 }}>▶ Start</button>
                        )}
                        {wo.status==='in_progress' && (
                          <button onClick={() => updateStatus(wo.id,'completed')} style={{ ...btnSm, background:green+'22', color:green, border:`1px solid ${green}44`, fontSize:11 }}>✓ Complete</button>
                        )}
                        {!['completed','cancelled'].includes(wo.status) && (
                          <button onClick={() => { setCostForm({...blankCost, work_order:wo.id}); setShowCostForm(true); }}
                            style={{ ...btnSm, background:gold+'18', color:gold, border:`1px solid ${gold}44`, fontSize:11 }}>+ Cost</button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ PM TAB ══════════════════════════════════════════════════════ */}
      {tab === 'pm' && (
        <div style={{ padding:'0 20px' }}>
          {overduePMs.length>0 && (
            <div style={{ background:`${orange}12`, border:`1px solid ${orange}38`, borderRadius:8, padding:'10px 16px', marginBottom:14, display:'flex', gap:10, alignItems:'center' }}>
              <span style={{ fontSize:18 }}>⚠️</span>
              <span style={{ color:orange, fontWeight:700 }}>{overduePMs.length} PM schedule{overduePMs.length>1?'s':''} overdue</span>
            </div>
          )}
          {pmSchedules.length===0 ? <Empty text="No PM schedules yet — create one above" /> : (
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {pmSchedules.map(pm => {
                const isOverdue = pm.is_active && pm.next_due < today;
                const freqObj = PM_FREQ.find(f=>f.value===pm.frequency);
                return (
                  <div key={pm.id} style={{
                    background:'var(--surface)', borderRadius:12, padding:'14px 16px',
                    borderLeft:`4px solid ${isOverdue?red:green}`,
                    border:`1px solid rgba(255,255,255,0.05)`, borderLeftWidth:4, borderLeftColor:isOverdue?red:green,
                    display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:14,
                  }}>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:6 }}>
                        <span style={{ fontWeight:700, fontSize:14, color:'var(--text)' }}>{pm.title}</span>
                        {freqObj && (
                          <span style={{ background:gold+'18', color:gold, borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:600 }}>
                            {freqObj.label}
                          </span>
                        )}
                        {isOverdue && <span style={{ background:red+'22', color:red, borderRadius:20, padding:'2px 9px', fontSize:11, fontWeight:700 }}>⚠ OVERDUE</span>}
                        {!isOverdue && pm.is_active && <span style={{ background:green+'18', color:green, borderRadius:20, padding:'2px 9px', fontSize:11 }}>✓ Active</span>}
                      </div>
                      <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:12, color:'var(--textMuted)' }}>
                        <span style={{ color:isOverdue?red:green, fontWeight:isOverdue?700:400 }}>
                          📅 Next: {pm.next_due}
                        </span>
                        {pm.last_done && <span>✓ Last done: {pm.last_done}</span>}
                        {pm.asset_name && <span>⚙️ {pm.asset_name}</span>}
                        {pm.estimated_cost > 0 && <span>Est. cost: {fmtKES(pm.estimated_cost)}</span>}
                        {pm.assigned_to_name && <span>👤 {pm.assigned_to_name}</span>}
                      </div>
                      {pm.description && <div style={{ color:'var(--textMuted)', fontSize:12, marginTop:6 }}>{pm.description}</div>}
                    </div>
                    <button onClick={() => markPMDone(pm.id)} style={{ ...btnSm, background:green+'18', color:green, border:`1px solid ${green}44`, minWidth:100, fontSize:12, flexShrink:0 }}>
                      ✓ Mark Done
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ COSTS TAB ═══════════════════════════════════════════════════ */}
      {tab === 'costs' && (
        <div style={{ padding:'0 20px' }}>
          {workOrders.filter(wo=>wo.cost_items?.length>0).length===0
            ? <Empty text="No maintenance costs recorded yet" />
            : workOrders.filter(wo=>wo.cost_items?.length>0).map(wo => (
              <div key={wo.id} style={{ background:'var(--surface)', borderRadius:12, padding:'14px 16px', marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, alignItems:'center' }}>
                  <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                    <span style={{ fontWeight:700, color:gold, fontFamily:'monospace' }}>{wo.ref}</span>
                    <span style={{ color:'var(--text)', fontWeight:600 }}>{wo.title}</span>
                    <span style={{ background:STATUS_COLOR[wo.status]+'22', color:STATUS_COLOR[wo.status], borderRadius:20, padding:'1px 8px', fontSize:11 }}>{wo.status}</span>
                  </div>
                  <span style={{ fontWeight:800, color:red, fontSize:16 }}>{fmtKES(wo.actual_cost)}</span>
                </div>
                {wo.cost_items.map(c=>(
                  <div key={c.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:13 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                      <span style={{ background:'rgba(255,255,255,0.06)', borderRadius:6, padding:'2px 8px', fontSize:11, color:'var(--textMuted)' }}>{c.cost_type}</span>
                      <span style={{ color:'var(--text)' }}>{c.description}</span>
                      {c.vendor_name && <span style={{ color:'var(--textMuted)', fontSize:11 }}>· {c.vendor_name}</span>}
                    </div>
                    <span style={{ fontWeight:700, color:'var(--text)' }}>{fmtKES(c.amount)}</span>
                  </div>
                ))}
              </div>
            ))
          }
        </div>
      )}

      {/* ══ SUMMARY TAB ═════════════════════════════════════════════════ */}
      {tab==='summary' && summary && (
        <div style={{ padding:'0 20px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:16, marginBottom:24 }}>
            {[
              ['Total Work Orders',     summary.total,          gold,   '🔧'],
              ['Overdue',               summary.overdue,        summary.overdue>0?red:green, '⏰'],
              ['Critical Open',         summary.critical_open,  summary.critical_open>0?red:green, '🚨'],
              ['PM Overdue',            summary.pm_overdue,     summary.pm_overdue>0?orange:green, '📅'],
              ['Total Cost (completed)', fmtKES(summary.total_cost_ytd), blue, '💰'],
            ].map(([l,v,c,icon]) => (
              <div key={l} style={{ background:'var(--surface)', borderRadius:12, padding:'16px 18px', textAlign:'center' }}>
                <div style={{ fontSize:24, marginBottom:6 }}>{icon}</div>
                <div style={{ fontWeight:800, fontSize:22, color:c, marginBottom:4 }}>{v}</div>
                <div style={{ color:'var(--textMuted)', fontSize:12 }}>{l}</div>
              </div>
            ))}
          </div>
          <Card title="Work Orders by Status">
            {(summary.by_status||[]).map(s => (
              <div key={s.status} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'9px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span>{STATUS_ICON[s.status]}</span>
                  <span style={{ color:STATUS_COLOR[s.status]||'var(--textMuted)', fontWeight:600 }}>{s.status.replace('_',' ').toUpperCase()}</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <div style={{ height:8, width:Math.max(20,(s.count/summary.total)*200), background:STATUS_COLOR[s.status]||'#555', borderRadius:4 }} />
                  <span style={{ fontWeight:700, color:'var(--text)', minWidth:24, textAlign:'right' }}>{s.count}</span>
                </div>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}
